import Table from "../../../components/RecordTable";
import DoctorApplications from "./DoctorApplications";
import useAutoRefresh from "../../../hooks/useAutoRefresh";
import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Tooltip,
  Dropdown,
  Avatar,
  Typography,
  Switch,
  Row,
  Col,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  LockOutlined,
  UnlockOutlined,
  EyeOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { userAPI, doctorSettingsAPI } from "../../../services/api";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { Option } = Select;

const Doctors = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const navigate = useNavigate();
  const [specialties, setSpecialties] = useState([]);
  const [qualifications, setQualifications] = useState([]);

  const fetchDoctorSettings = async () => {
    try {
      const res = await doctorSettingsAPI.getSettings();
      setSpecialties(res.data?.data?.specialties || res.data?.specialties || []);
      setQualifications(res.data?.data?.qualifications || res.data?.qualifications || []);
    } catch {
      // silently fail — dropdowns will just be empty
    }
  };

  const [search, setSearch] = useState("");

  const fetchDoctors = async () => {
    setLoading(true);
    try {
      const response = await userAPI.getAll({
        role: "doctor",
        page: pagination.current,
        limit: pagination.pageSize,
        ...(search.trim() ? { search: search.trim() } : {}),
      });

      setDoctors(response.data?.users || []);
      setPagination({
        ...pagination,
        total: response.data?.pagination?.total ?? 0,
      });
    } catch (error) {
      message.error("Failed to fetch doctors");
    } finally {
      setLoading(false);
    }
  };

  useAutoRefresh(() => fetchDoctors());

  useEffect(() => {
    fetchDoctors();
    fetchDoctorSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, search]);

  const handleSubmit = async (values) => {
    try {
      const doctorData = {
        ...values,
        role: "doctor",
        qualifications: Array.isArray(values.qualifications)
          ? values.qualifications.join(", ")
          : values.qualifications || "",
      };

      if (editingDoctor) {
        if (!doctorData.password) delete doctorData.password;
        await userAPI.update(editingDoctor._id, doctorData);
        message.success("Doctor updated successfully");
      } else {
        await userAPI.create(doctorData);
        message.success("Doctor created successfully");
      }
      setModalVisible(false);
      fetchDoctors();
    } catch (error) {
      message.error(error.message || "Operation failed");
    }
  };

  const handleSuspend = async (doctor) => {
    try {
      await userAPI.update(doctor._id, { isActive: !doctor.isActive });
      message.success(
        doctor.isActive
          ? "Doctor account suspended"
          : "Doctor account activated"
      );
      fetchDoctors();
    } catch (error) {
      message.error("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    try {
      await userAPI.delete(id);
      message.success("Doctor deleted successfully");
      fetchDoctors();
    } catch (error) {
      message.error("Delete failed");
    }
  };

  const handleEdit = (doctor) => {
    form.resetFields();
    setEditingDoctor(doctor);
    // qualifications stored as comma-separated string; split for multi-select
    const quals = doctor.qualifications
      ? doctor.qualifications.split(",").map((q) => q.trim()).filter(Boolean)
      : [];
    form.setFieldsValue({
      ...doctor,
      specialties: doctor.specialties || [],
      qualifications: quals,
    });
    setModalVisible(true);
  };
  const handleViewDashboard = (rec) => {
    navigate(`/superadmin/doctors/${rec._id}`);
  };
  const columns = [
    { title: "Doctor ID", dataIndex: "_id", width: 180, render: (id, row) => <Text code copyable>{row.publicId || id}</Text> },
    {
      title: "Doctor",
      key: "doctor",
      render: (_, record) => (
        <Space>
          <Avatar
            size={50}
            src={record.profileImage}
            icon={<UserOutlined />}
            style={{ backgroundColor: "#0A6EBD" }}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{record.name}</div>
            <div style={{ fontSize: 12, color: "#666" }}>{record.email}</div>
            <div style={{ fontSize: 12, color: "#999" }}>{record.phone}</div>
          </div>
        </Space>
      ),
    },
    {
      title: "Specialties",
      dataIndex: "specialties",
      key: "specialties",
      render: (specialties) => (
        <div>
          {specialties?.slice(0, 2).map((spec, idx) => (
            <Tag key={idx} color="blue" style={{ marginBottom: 4 }}>
              {spec}
            </Tag>
          ))}
          {specialties?.length > 2 && (
            <Tag color="default">+{specialties.length - 2} more</Tag>
          )}
        </div>
      ),
    },
    {
      title: "Qualifications",
      dataIndex: "qualifications",
      key: "qualifications",
      ellipsis: true,
      render: (qual) => <Text ellipsis>{qual || "N/A"}</Text>,
    },
    {
      title: "Experience",
      dataIndex: "experience",
      key: "experience",
      render: (exp) => (exp ? `${exp} years` : "N/A"),
    },
    {
      title: "Consultation Fee",
      dataIndex: "consultationFee",
      key: "consultationFee",
      render: (fee) => (fee ? `₹${fee}` : "N/A"),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive, record) => (
        <Tag color={isActive ? "success" : "error"}>
          {!isActive ? "Suspended" : record.doctorApprovalStatus && record.doctorApprovalStatus !== "approved" ? `Application: ${record.doctorApprovalStatus}` : "Active"}
        </Tag>
      ),
    },

    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 150,
      render: (_, record) => (
        <Space size={6} wrap={false}>
          <Button
            size="small"
            icon={<EyeOutlined />}
            disabled={record.doctorApprovalStatus && record.doctorApprovalStatus !== "approved"}
            onClick={() => handleViewDashboard(record)}
          >
            View
          </Button>
          <Tooltip title="Edit">
            <Button size="small" type="text" aria-label="Edit doctor" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                { key: "suspend", icon: record.isActive ? <LockOutlined /> : <UnlockOutlined />, label: record.isActive ? "Suspend" : "Activate" },
                { type: "divider" },
                { key: "delete", icon: <DeleteOutlined />, label: "Delete", danger: true },
              ],
              onClick: ({ key }) =>
                key === "suspend"
                  ? handleSuspend(record)
                  : Modal.confirm({
                      title: `Delete ${record.name}?`,
                      content: "This removes the doctor account. It cannot be undone.",
                      okText: "Delete",
                      okButtonProps: { danger: true },
                      onOk: () => handleDelete(record._id),
                    }),
            }}
          >
            <Button size="small" type="text" aria-label="More actions" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <DoctorApplications />
      <div className="page-heading"
        style={{
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Doctor Management
          </Title>
          <Text type="secondary">
            Manage doctors, profiles, and account status
          </Text>
        </div>
        <Space>
          <Input
            allowClear
            size="large"
            style={{ width: 280 }}
            placeholder="Name / Specialty"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

        </Space>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={doctors}
          rowKey="_id"
          loading={loading}
          pagination={pagination}
          onChange={(newPagination) => setPagination(newPagination)}
          // scroll={{ x: 1200 }}
          scroll={{ x: "max-content" }}
          components={{
            body: {
              wrapper: (props) => (
                <tbody {...props} style={{ transition: "none" }} />
              ),
            },
          }}
        />
      </Card>

      <Modal
        title={editingDoctor ? "Edit Doctor" : "Add New Doctor"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Full Name"
                rules={[{ required: true }]}
              >
                <Input size="large" placeholder="Dr. John Doe" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[{ required: true, type: "email" }]}
              >
                <Input size="large" placeholder="doctor@example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[{ required: true }]}
              >
                <Input size="large" placeholder="+91 9876543210" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="specialties"
                label="Specialties"
                rules={[{ required: true }]}
              >
                <Select
                  mode="multiple"
                  size="large"
                  placeholder="Select specialties"
                >
                  {specialties.map((spec) => (
                    <Option key={spec} value={spec}>
                      {spec}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="experience"
                label="Experience (years)"
                rules={[{ required: true }]}
              >
                <Input type="number" size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="consultationFee"
                label="Fee (₹)"
                rules={[{ required: true }]}
              >
                <Input type="number" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="qualifications"
            label="Qualifications"
            rules={[{ required: true }]}
          >
            <Select
              mode="multiple"
              size="large"
              placeholder="Select qualifications"
            >
              {qualifications.map((qual) => (
                <Option key={qual} value={qual}>
                  {qual}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {!editingDoctor && (
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, min: 6 }]}
            >
              <Input.Password size="large" />
            </Form.Item>
          )}

          <Form.Item
            name="isActive"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Suspended" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button size="large" onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" size="large" htmlType="submit">
                {editingDoctor ? "Update" : "Create"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Doctors;
